import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";
import { AnnouncementsPage } from "@/pages/AnnouncementsPage";
import { IndexAdminPage } from "@/pages/IndexAdminPage";
import { LoginPage } from "@/pages/LoginPage";
import { ModelsPage } from "@/pages/ModelsPage";
import { MonitorPage } from "@/pages/MonitorPage";
import { ReportsPage } from "@/pages/ReportsPage";
import { StatsPage } from "@/pages/StatsPage";
import { UnansweredPage } from "@/pages/UnansweredPage";
import { Skeleton } from "@/components/ui/skeleton";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5_000,
      refetchOnWindowFocus: false,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { state } = useAuth();
  if (state.status === "loading") {
    return (
      <div className="flex min-h-full items-center justify-center p-12">
        <div className="space-y-3">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-32 w-80" />
        </div>
      </div>
    );
  }
  if (state.status !== "authenticated") {
    return <LoginPage />;
  }
  return <>{children}</>;
}

const basename = "/";

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter basename={basename}>
          <Routes>
            <Route
              element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="/monitor" replace />} />
              <Route path="/monitor" element={<MonitorPage />} />
              <Route path="/index-admin" element={<IndexAdminPage />} />
              <Route path="/stats" element={<StatsPage />} />
              <Route path="/unanswered" element={<UnansweredPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/announcements" element={<AnnouncementsPage />} />
              <Route path="/models" element={<ModelsPage />} />
              <Route path="*" element={<Navigate to="/monitor" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
